import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Spinner, EmptyState } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Warehouse as WhIcon, PackageX, Recycle, ArrowRightLeft } from "lucide-react";

export default function Warehouse() {
  const { user } = useAuth();
  const [tab, setTab] = useState("reserved");
  const [reserved, setReserved] = useState([]);
  const [general, setGeneral] = useState([]);
  const [salvage, setSalvage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/warehouse/reserved"), api.get("/warehouse/general"), api.get("/warehouse/salvage")])
      .then(([r, g, s]) => { setReserved(r.data); setGeneral(g.data); setSalvage(s.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const transfer = async (row) => {
    try {
      await api.post("/warehouse/transfer", { job_id: row.job_id, part_number: row.part_number, part_name: row.part_name, qty: row.remaining_qty });
      toast.success("Sisa part dipindahkan ke General Stock");
      load();
    } catch (err) { toast.error(apiError(err, "Gagal transfer")); }
  };

  const canTransfer = user.role === "owner" || user.role === "warehouse_out";

  return (
    <div>
      <PageHeader title="Warehouse" subtitle="Reserved per Nopol, General Stock, dan Salvage Report." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="reserved" data-testid="wh-tab-reserved">Reserved by Nopol</TabsTrigger>
          <TabsTrigger value="general" data-testid="wh-tab-general">General Stock</TabsTrigger>
          <TabsTrigger value="salvage" data-testid="wh-tab-salvage">Salvage Report</TabsTrigger>
        </TabsList>
        {loading ? <Spinner /> : (
          <>
            <TabsContent value="reserved">
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="wh-search" placeholder="Cari Nopol / No Claim / Part Number / Nama Part" className="neon-input w-full h-10 rounded-lg px-3 mb-3 text-sm" />
              {(() => {
                const term = q.trim().toLowerCase();
                const filtered = reserved.filter((r) => !term || [r.nopol, r.claim_number, r.part_number, r.part_name].some((v) => (v || "").toLowerCase().includes(term)));
                if (filtered.length === 0) return <EmptyState title="Belum ada reserved stock" icon={WhIcon} />;
                const groups = {};
                filtered.forEach((r) => { const k = `${r.claim_number || "NO CLAIM"}|${r.nopol}`; (groups[k] = groups[k] || []).push(r); });
                return (
                  <div className="grid gap-3">
                    {Object.entries(groups).map(([k, gr]) => {
                      const [claim, nopol] = k.split("|");
                      const done = gr.filter((x) => x.received_qty > 0).length;
                      return (
                        <Card key={k} className="p-3" data-testid={`wh-group-${nopol}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-head font-bold text-white"><span className="font-mono-data">{claim}</span> <span className="text-gray-400">•</span> <span className="font-mono-data">{nopol}</span></div>
                            <span className="text-xs text-gray-400">{done} part diterima</span>
                          </div>
                          <div className="grid gap-2">
                            {gr.map((r, i) => (
                              <div key={i} className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2" data-testid={`reserved-${nopol}-${i}`}>
                                <div>
                                  <div className="font-mono-data text-sm font-semibold text-white">{r.part_number} — {r.part_name}</div>
                                  <div className="text-xs text-gray-400">Received {r.received_qty} • Installed {r.installed_qty} • Remaining {r.remaining_qty}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <StatusBadge status={r.status} />
                                  {canTransfer && r.remaining_qty > 0 && r.status === "READY TO TRANSFER" && (<Button size="sm" variant="outline" onClick={() => transfer(r)}><ArrowRightLeft size={14} className="mr-1" />Transfer</Button>)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                );
              })()}
            </TabsContent>
            <TabsContent value="general">
              {general.length === 0 ? <EmptyState title="General stock kosong" icon={PackageX} /> : (
                <div className="grid gap-2">
                  {general.map((g) => (
                    <Card key={g.id} className="p-3 flex items-center justify-between" data-testid={`general-${g.id}`}>
                      <div>
                        <div className="font-mono-data font-semibold">{g.part_number} — {g.part_name}</div>
                        <div className="text-xs text-gray-400">Asal: {g.original_nopol} • {g.condition} • {new Date(g.created_at).toLocaleDateString("id-ID")}</div>
                      </div>
                      <div className="font-head text-xl font-bold text-[var(--gold)]">{g.qty_available}</div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="salvage">
              {salvage.length === 0 ? <EmptyState title="Belum ada salvage" icon={Recycle} /> : (
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                        <tr><th className="text-left px-3 py-2">Nopol</th><th className="text-left px-3 py-2">Part</th><th className="text-center px-3 py-2">Qty</th><th className="text-left px-3 py-2">Technician</th><th className="text-left px-3 py-2">Installed</th></tr>
                      </thead>
                      <tbody>
                        {salvage.map((s) => (
                          <tr key={s.id} className="border-t border-gray-100" data-testid={`salvage-${s.id}`}>
                            <td className="px-3 py-2 font-mono-data font-semibold">{s.nopol}</td>
                            <td className="px-3 py-2">{s.part_number} {s.part_name}</td>
                            <td className="text-center px-3 py-2">{s.qty_installed}</td>
                            <td className="px-3 py-2">{s.technician || "—"}</td>
                            <td className="px-3 py-2">{(s.installed_date || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
